import React, {useEffect, useMemo, useRef} from "react";
import PreFooter from "@components/Prefooter/PreFooter";
import PageHeader from "@components/Page/PageHeader";
import {findFirstSection, getCmsPage} from "@lib/Cms/CmsUtils";
import {SECTION_TYPE} from "@lib/Cms/CmsModels";
import Page from "@components/Page/Page";
import PageContent from "@components/Page/PageContent";
import PageSection from "@components/Page/PageSection";
import {useCatalog, useProductFilters} from "@lib/Catalog/CatalogHooks";
import "./CatalogPage.scss";
import KvBanner from "@components/KvBanner/KvBanner";
import CatalogFilters from "@pages/Catalog/Components/CatalogFilters";
import {useProjectTranslations} from "@i18n/i18nUtils";
import ProductCard from "@components/Product/ProductCard/ProductCard";
import BoostsPreview from "@components/Boost/BoostsPreview/BoostsPreview";

export default function CatalogPage() {

    const {t} = useProjectTranslations();
    const [isMobile, setIsMobile] = React.useState(false);

    const {sections, banner, ...page} = useMemo(() => getCmsPage("catalog") ?? {}, []);

    const defaultSection = useMemo(() => findFirstSection(sections, SECTION_TYPE.DEFAULT), [sections]);
    const preFooterSection = useMemo(() => findFirstSection(sections, SECTION_TYPE.PRE_FOOTER), [sections]);
    const boostSection = useMemo(() => findFirstSection(sections, SECTION_TYPE.BOOSTS_PREVIEW), [sections]);

    const resultsTopRef = useRef<HTMLDivElement | null>(null);

    const {data: catalog = [], isLoading, isError} = useCatalog();

    React.useEffect(() => {
        const m = window.matchMedia("(max-width: 1024px)");
        const handler = () => setIsMobile(m.matches);
        handler();
        m.addEventListener("change", handler);
        return () => m.removeEventListener("change", handler);
    }, []);

    const {
        state,
        setState,
        filtered,
        categories,
        brands,
        minBound,
        maxBound,
        hasActiveFilters,
        reset,
    } = useProductFilters(catalog);

    const userChangedFiltersRef = React.useRef(false);

    const markUserFilterChange = React.useCallback(() => {
        userChangedFiltersRef.current = true;
    }, []);

    const filtersProps = {
        categories,
        brands,
        state,
        setState,
        hasActiveFilters,
        onReset: () => {
            markUserFilterChange();
            reset();
        },
        minAll: minBound,
        maxAll: maxBound,
        markUserFilterChange
    } as const;

    useEffect(() => {
        if (!userChangedFiltersRef.current) return;
        userChangedFiltersRef.current = false;
        resultsTopRef.current?.scrollIntoView({behavior: "smooth", block: "start"});
    }, [filtered.length]);

    return (
        <>
            <Page>

                {!!banner &&
                    <KvBanner name={"catalog"} banner={banner}/>
                }

                <div ref={resultsTopRef} />
                <PageHeader cms={page}/>

                <PageContent>

                    <PageSection bg={defaultSection?.options?.bgType}>

                        <div className="catalog">
                            {!isMobile && !isLoading && !isError &&
                                <div className="catalog__filters--desktop">
                                    <CatalogFilters {...filtersProps} />
                                </div>
                            }

                            <section className="catalog__results" aria-label={t("catalog:results:title")}>
                                <div className="catalog__results__head">

                                    {isMobile && !isLoading && !isError &&
                                        <details className="catalog__filtersToggle">
                                            <summary className="catalog__filtersToggle__summary tp-xs semibold">
                                                {t("catalog:filters:title")}
                                                {hasActiveFilters && (
                                                    <span className="catalog__filtersToggle__badge"
                                                          aria-label={t("catalog:filters:reset")}>
                                                  •
                                                </span>
                                                )}
                                                <span className="catalog__filtersToggle__chevron"
                                                      aria-hidden="true">▾</span>
                                            </summary>

                                            <div className="catalog__filtersToggle__content">
                                                <CatalogFilters {...filtersProps} hideTitle/>
                                            </div>
                                        </details>
                                    }
                                </div>

                                {isLoading && (
                                    <div className="catalog__state tp-xs light">
                                        {t("loading")}
                                    </div>
                                )}

                                {isError && (
                                    <div className="catalog__state tp-xs">
                                        {t("catalog:error")}
                                    </div>
                                )}

                                {!isLoading && !isError && filtered.length === 0 && (
                                    <div className="catalog__state tp-xs light">
                                        {t("catalog:empty")}
                                    </div>
                                )}

                                {!isLoading && !isError && filtered.length > 0 && (
                                    <div className="catalog__grid">
                                        {filtered.map((p) => (
                                            <ProductCard
                                                key={`${p.categoryName}-${p.title}-${p.points}`}
                                                image={p.image}
                                                categoryName={p.categoryName}
                                                title={p.title}
                                                points={p.points}
                                                linkProps={p.linkProps}
                                            />
                                        ))}
                                    </div>
                                )}
                            </section>
                        </div>

                    </PageSection>

                </PageContent>

            </Page>

            {!!boostSection && <BoostsPreview cms={boostSection}/>}

            {!!preFooterSection && <PreFooter cms={preFooterSection}/>}

        </>
    );
}
